import React from "react";
import { motion } from "framer-motion";
import { YARNS, YARN_ORDER } from "../lib/yarns";

/**
 * YarnsStrip — a compact, above-the-fold "key" for the four yarns.
 *
 * Sits between the hero copy and the loom. Each pill shows the yarn's mini
 * yarn-ball icon, its label, and a one-line promise. Clicking any pill
 * scrolls to the full Yarns Explainer section below.
 */
export default function YarnsStrip() {
  return (
    <section
      className="relative mt-8 md:mt-10"
      aria-label="The four yarns at a glance"
      data-testid="yarns-strip"
    >
      <p
        className="text-center text-xs uppercase tracking-[0.22em] text-[#7a5816] font-medium mb-4"
        data-testid="yarns-strip-eyebrow"
      >
        Four threads, one weave
      </p>
      <div className="flex flex-wrap justify-center items-stretch gap-2.5 md:gap-3">
        {YARN_ORDER.map((key, i) => {
          const yarn = YARNS[key];
          return (
            <motion.a
              key={key}
              href="#yarns"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 + i * 0.06 }}
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="group inline-flex items-center gap-2.5 pl-2 pr-4 py-1.5 rounded-full border-2 transition-shadow hover:shadow-[0_8px_18px_rgba(60,40,10,0.10)]"
              style={{
                backgroundColor: yarn.pastel,
                borderColor: "#3d2a14",
                color: "#1a1a1a",
              }}
              data-testid={`yarn-strip-${key}`}
              aria-label={`${yarn.label} — ${yarn.promise}. See full explainer.`}
            >
              {/* Mini yarn ball */}
              <svg
                viewBox="-22 -22 44 44"
                width="28"
                height="28"
                aria-hidden
                className="shrink-0"
              >
                <circle r="18" fill={yarn.deep} stroke="#3d2a14" strokeWidth="1.6" />
                {[0, 1, 2].map((n) => (
                  <path
                    key={n}
                    d={`M -16 ${-8 + n * 8} Q 0 ${-4 + n * 8}, 16 ${-8 + n * 8}`}
                    stroke={yarn.accent}
                    strokeWidth="0.9"
                    fill="none"
                    opacity="0.7"
                  />
                ))}
                <ellipse cx="-6" cy="-7" rx="3.5" ry="2" fill="#ffffff" opacity="0.35" />
              </svg>
              <span className="leading-tight">
                <span
                  className="block text-sm font-semibold"
                  style={{ fontFamily: "Rubik, sans-serif" }}
                >
                  {yarn.label}
                </span>
                <span
                  className="block text-[11px] md:text-xs text-[#1a1a1a]/75"
                >
                  {yarn.promise}
                </span>
              </span>
            </motion.a>
          );
        })}
      </div>
    </section>
  );
}
